import {Component, DestroyRef, effect, inject, input, output, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ImagesService} from '../../../../services/general/images.service';
import {CustomInputComponent} from "../../../input/custom-input/custom-input.component";
import {CustomButtonComponent} from "../../../buttons/custom-button/custom-button.component";
import {Institution} from '../../../../interfaces/responses/institution.interface';
import {InstitutionService} from '../../../../services/institution/institution.service';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {ProductService} from '../../../../services/products/product.service';
import {Product, ProductResponseInterfaces} from '../../../../interfaces/responses/product-response.interfaces';
import {SelectOptionComponent} from "../../../shared/select-option/select-option.component";
import {ToastService} from '../../../../services/general/toast.service';
import {CategoryService} from '../../../../services/categories/category.service';
import {Category} from '../../../../interfaces/responses/category.interface';

@Component({
  selector: 'app-create-new-product',
  imports: [
    CustomInputComponent,
    CustomButtonComponent,
    CommonModule,
    ReactiveFormsModule,
    SelectOptionComponent,
  ],
  templateUrl: './create-new-product.component.html',
  styleUrl: './create-new-product.component.scss'
})
export class CreateNewProductComponent {

  _images = inject(ImagesService);
  allInstitutions: Institution[] = [];
  selectedInstitutions: Institution[] = []
  institutionService = inject(InstitutionService);
  categoryService = inject(CategoryService);
  allCategories: Category[] = [];
  categoryMap: Map<string, string> = new Map(); // Maps category name to category ID
  _fb = inject(FormBuilder)
  productService = inject(ProductService)
  productRespponse: ProductResponseInterfaces = {}
  isLoading: boolean = false;
  _toast = inject(ToastService);
  product = input<Product | null>(null);

  disbursementTypes = [
    {
      name: 'AUTOMATIC'
    },
    {
      name: 'MANUAL'
    }
  ];

  repaymentModes = [
    {
      name: 'DAILY'
    },
    {
      name: 'WEEKLY'
    },
    {
      name: 'MONTHLY'
    }
  ]

  tenureTypes = [
    {
      name: 'DAILY'
    },
    {
      name: 'WEEKLY'
    },
    {
      name: 'MONTHLY'
    }
  ]

  monthlyTenureOptions: { name: string }[] = Array.from({length: 12}, (_, i) => ({name: String(i + 1)}));
  weeklyTenureOptions: { name: string }[] = Array.from({length: 4}, (_, i) => ({name: String(i + 1)}));

  autoRepaymentValue: boolean = false;
  maxTenureDisabled = signal(true);
  maxTenureOptions = signal<{ name: string }[]>(this.monthlyTenureOptions);
  maxTenureLabel = signal('Maximum Tenure (months)');

  private _destroyRef = inject(DestroyRef);

  categories: { name: string }[] = [];

  formData: FormGroup = this._fb.group({
    name: ['', [Validators.required]],
    code: ['', [Validators.required]],
    categoryId: ['', [Validators.required]],
    disbursementType: ['', [Validators.required]],
    minAmount: [null, [Validators.required]],
    repaymentMode: [{value: '', disabled: true}, [Validators.required]],
    tenureType: ['', [Validators.required]],
    maxTenure: [{value: null, disabled: true}],
    autoRepayment: [false],
    minAgeLimit: [null, [Validators.required]],
    maxAgeLimit: [null, [Validators.required]],
    interestRate: [null],
    processingFeeRate: [null],
  })

  // React to changes in the 'product' input and prefill/reset the form accordingly.
  private prefillFormFromProduct = effect(() => {
    const prod = this.product(); // depend on input signal
    if (prod) {
      // Use category name directly from product.category
      // Note: form control 'categoryId' actually stores the category name, not the ID
      // Only set if categories are loaded to ensure the value matches an available option
      let categoryName = '';
      if (prod.category?.name && this.allCategories.length > 0) {
        // Verify the category exists in loaded categories before setting
        const categoryExists = this.allCategories.some(cat => cat.name === prod.category?.name);
        if (categoryExists) {
          categoryName = prod.category.name;
        }
      }
      // If categories aren't loaded yet, don't set categoryName
      // prefillCategory will handle it when categories load

      // Set tenureType silently first so the valueChanges listener doesn't wipe out the prefilled maxTenure below
      this.updateMaxTenureState(prod.tenureType ?? '');
      this.autoRepaymentValue = prod.autoRepayment ?? false;
      this.formData.patchValue({
        name: prod.name ?? '',
        code: prod.code ?? '',
        categoryId: categoryName,
        disbursementType: prod.disbursementType ?? '',
        minAmount: prod.minAmount ?? null,
        repaymentMode: prod.repaymentMode ?? '',
        tenureType: prod.tenureType ?? '',
        maxTenure: prod.maxTenure ?? null,
        autoRepayment: prod.autoRepayment ?? false,
        minAgeLimit: prod.minAgeLimit ?? null,
        maxAgeLimit: prod.maxAgeLimit ?? null,
        interestRate: prod.interestRate ?? null,
        processingFeeRate: prod.processingFeeRate ?? prod.processingFee ?? null,
      }, {emitEvent: false});

      // Populate selected institutions if institutionIds are available
      if (prod.institutionIds && prod.institutionIds.length > 0) {
        this.prefillInstitutions(prod.institutionIds);
      }

      this.formData.markAsPristine();
      this.formData.markAsUntouched();
    } else {
      // reset to empty values explicitly
      this.updateMaxTenureState('');
      this.autoRepaymentValue = false;
      this.formData.reset({
        name: '',
        code: '',
        categoryId: '',
        disbursementType: '',
        minAmount: null,
        repaymentMode: '',
        tenureType: '',
        maxTenure: null,
        autoRepayment: false,
        minAgeLimit: null,
        maxAgeLimit: null,
        interestRate: null,
        processingFeeRate: null,
      }, {emitEvent: false});
      this.selectedInstitutions = [];
      this.formData.markAsPristine();
      this.formData.markAsUntouched();
    }
  });

  ngOnInit(): void {
    this.getAllInstitutions();
    this.getAllCategories();
    // If product is already set when component initializes, wait for categories to load
    const prod = this.product();
    if (prod && prod.categoryId) {
      // Categories will be loaded in getAllCategories, which will call prefillCategory
    }

    this.formData.get('tenureType')?.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((tenureType: string) => {
        this.updateMaxTenureState(tenureType);
        this.formData.patchValue({
          maxTenure: null,
          ...(tenureType && {repaymentMode: tenureType}),
        });
      });
  }

  private updateMaxTenureState(tenureType: string) {
    const control = this.formData.get('maxTenure');
    const requiresMaxTenure = tenureType === 'MONTHLY' || tenureType === 'WEEKLY';
    if (requiresMaxTenure) {
      control?.enable({emitEvent: false});
      control?.setValidators([Validators.required]);
    } else {
      control?.disable({emitEvent: false});
      control?.clearValidators();
    }
    control?.updateValueAndValidity({emitEvent: false});
    this.maxTenureDisabled.set(!requiresMaxTenure);
    this.maxTenureOptions.set(tenureType === 'WEEKLY' ? this.weeklyTenureOptions : this.monthlyTenureOptions);
    this.maxTenureLabel.set(tenureType === 'WEEKLY' ? 'Maximum Tenure (weeks)' : 'Maximum Tenure (months)');
  }

  getAllInstitutions() {
    this.institutionService.getPaginatedInstitutions({paginate: false}).subscribe({
      next: (resp) => {
        this.allInstitutions = resp.data;
        // If product is set and has institutionIds, populate selected institutions after loading
        const prod = this.product();
        if (prod && prod.institutionIds && prod.institutionIds.length > 0) {
          this.prefillInstitutions(prod.institutionIds);
        }
      },
      error: () => {}
    })
  }

  private prefillInstitutions(institutionIds: string[] | undefined) {
    if (!institutionIds || institutionIds.length === 0) return;
    if (this.allInstitutions.length === 0) return;

    // Find institutions that match the institutionIds
    this.selectedInstitutions = this.allInstitutions.filter(inst =>
      inst.id && institutionIds.includes(inst.id)
    );
  }

  getAllCategories() {
    this.categoryService.fetchCategories({paginate: false}).subscribe({
      next: (resp) => {
        this.allCategories = resp.data || [];
        // Map categories for select-option component and create name-to-id mapping
        this.categories = this.allCategories.map(cat => {
          this.categoryMap.set(cat.name, cat.id);
          return {name: cat.name};
        });
        // Always prefill category from product.category when categories are loaded
        // This ensures the correct category is selected
        const prod = this.product();
        if (prod && prod.category?.id) {
          this.prefillCategory(prod.category.id);
        }
      },
      error: () => {}
    })
  }

  private prefillCategory(categoryId: string) {
    if (!categoryId) return;
    // Find category by ID - ensure exact match
    const category = this.allCategories.find(cat => cat.id === categoryId);
    if (category) {
      // Set the category name in the form control (which stores the name, not the ID)
      // Use setTimeout to ensure the select component is ready
      setTimeout(() => {
        this.formData.patchValue({
          categoryId: category.name
        }, { emitEvent: false });
        // Mark as pristine to avoid validation issues
        this.formData.get('categoryId')?.markAsPristine();
      }, 0);
    } else {
      console.warn(`Category with ID ${categoryId} not found in loaded categories. Available categories:`, this.allCategories.map(c => ({ id: c.id, name: c.name })));
    }
  }

  toggleAutoRepayment() {
    this.autoRepaymentValue = !this.autoRepaymentValue;
    this.formData.patchValue({autoRepayment: this.autoRepaymentValue});
  }

  isInstitutionSelected(institution: Institution): boolean {
    return this.selectedInstitutions?.some((item: Institution) => item.id === institution.id);
  }

  onAddToSelectedInstitutions(insititution: Institution) {
    const alreadyAdded = this.selectedInstitutions.some(inst => inst.id === insititution.id)

    if (!alreadyAdded) {
      this.selectedInstitutions.push(insititution);
      this.isInstitutionSelected(insititution)
    }
  }

  onRemoveFromList(insititution: Institution) {
    this.selectedInstitutions = this.selectedInstitutions.filter(
      (inst) => inst.id !== insititution.id
    )
  }

  createNewProduct() {
    if (this.formData.invalid) {
      this.formData.markAllAsTouched();
      return;
    }
    if (this.product()) {
      this.updateProduct();
    } else {
      this.createProduct();
    }
  }

  private createProduct() {
    // Extract the list of institution IDs from the selected institutions
    const institutionIds = this.selectedInstitutions.map(inst => inst.id).filter((id): id is string => !!id);

    // Get categoryId from the selected category name
    const categoryName = this.formData.get('categoryId')?.value;
    const categoryId = this.categoryMap.get(categoryName) || '';

    // Combine the form data with the selected institution IDs and categoryId into a single request payload
    const {categoryId: _, ...formValues} = this.formData.getRawValue();
    const requestBody = {
      name: formValues.name,
      code: formValues.code,
      categoryId: categoryId,
      institutionIds: institutionIds,
      disbursementType: formValues.disbursementType,
      minAmount: Number(formValues.minAmount),
      repaymentMode: formValues.repaymentMode,
      maxTenure: Number(formValues.maxTenure),
      tenureType: formValues.tenureType,
      autoRepayment: formValues.autoRepayment,
      minAgeLimit: Number(formValues.minAgeLimit),
      maxAgeLimit: Number(formValues.maxAgeLimit),
      ...(formValues.interestRate != null && { interestRate: Number(formValues.interestRate) }),
      ...(formValues.processingFeeRate != null && { processingFeeRate: Number(formValues.processingFeeRate) }),
    };

    this.isLoading = true;
    this.productService.createProduct(requestBody).subscribe({
      next: (resp) => {
        this.productRespponse = resp;
        this.loadProduct();
        this.formData.reset();
        this._toast.success('Product created successfully');
        this.onCloseClick();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error creating product:', error);
        const serverMsg = error?.error?.message;
        const msg = Array.isArray(serverMsg)
          ? serverMsg.join('; ')
          : (serverMsg || error?.message || 'Error creating product');
        this._toast.error(msg);
        this.isLoading = false;
      }
    });
  }

  private updateProduct() {
    const prod = this.product();
    if (!prod?.id) return;

    // Extract the list of institution IDs from the selected institutions
    const institutionIds = this.selectedInstitutions.map(inst => inst.id).filter((id): id is string => !!id);

    // Get categoryId from the selected category name
    const categoryName = this.formData.get('categoryId')?.value;
    const categoryId = this.categoryMap.get(categoryName) || '';

    // Combine the form data with the selected institution IDs and categoryId into a single request payload
    const {categoryId: _, ...formValues} = this.formData.getRawValue();
    const payload = {
      name: formValues.name,
      code: formValues.code,
      categoryId: categoryId,
      institutionIds: institutionIds,
      disbursementType: formValues.disbursementType,
      minAmount: Number(formValues.minAmount),
      repaymentMode: formValues.repaymentMode,
      maxTenure: Number(formValues.maxTenure),
      tenureType: formValues.tenureType,
      autoRepayment: formValues.autoRepayment,
      minAgeLimit: Number(formValues.minAgeLimit),
      maxAgeLimit: Number(formValues.maxAgeLimit),
      ...(formValues.interestRate != null && { interestRate: Number(formValues.interestRate) }),
      ...(formValues.processingFeeRate != null && { processingFeeRate: Number(formValues.processingFeeRate) }),
    };

    this.isLoading = true;
    this.productService.updateProduct(prod.id, payload).subscribe({
      next: () => {
        this._toast.success('Product updated successfully');
        this.loadProduct();
        this.onCloseClick();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error updating product:', error);
        const serverMsg = error?.error?.message;
        const msg = Array.isArray(serverMsg)
          ? serverMsg.join('; ')
          : (serverMsg || error?.message || 'Error updating product');
        this._toast.error(msg);
        this.isLoading = false;
      }
    });
  }


  loadProductEvent = output<void>()

  loadProduct() {
    this.loadProductEvent.emit();
  }

  onCloseEvent = output<void>()

  onCloseClick() {
    // Reset form and selected institutions
    this.updateMaxTenureState('');
    this.autoRepaymentValue = false;
    this.formData.reset({
      name: '',
      code: '',
      categoryId: '',
      disbursementType: '',
      minAmount: null,
      repaymentMode: '',
      tenureType: '',
      maxTenure: null,
      autoRepayment: false,
      minAgeLimit: null,
      maxAgeLimit: null,
    });
    this.selectedInstitutions = [];
    this.formData.markAsPristine();
    this.formData.markAsUntouched();
    this.formData.updateValueAndValidity({onlySelf: false, emitEvent: false});
    this.onCloseEvent.emit()
  }


}
